import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Check, Trash2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useData } from "@/context/DataContext";
import { fmtDate, fmtEuro, isoDate } from "@/lib/format";
import { Empty } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { MAINT_KINDS, FormDialog, opts } from "@/components/fleet/fleetConst";

const vehicleOpts = (vehicles) => vehicles.map((v) => ({ value: v.id, label: `${v.plate} · ${v.model}` }));

export const MaintenanceTab = ({ vehicles, refreshKey, onChanged }) => {
  const { can } = useAuth();
  const { inCompany } = useData();
  const [raw, setRows] = useState([]);
  const rows = raw.filter((r) => inCompany(r.company_id));
  const [planOpen, setPlanOpen] = useState(false);
  const [plan, setPlan] = useState({});
  const [doneFor, setDoneFor] = useState(null);
  const [done, setDone] = useState({});
  const load = useCallback(() => api.get("/fleet/maintenance").then((r) => setRows(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load, refreshKey]);
  const call = async (fn, msg) => {
    try { await fn(); toast.success(msg); load(); onChanged(); return true; } catch (e) { toast.error(errMsg(e)); return false; }
  };
  const savePlan = async () => {
    const body = { ...plan, due_date: plan.due_date || null, due_km: plan.due_km || null };
    if (await call(() => api.post("/fleet/maintenance", body), "Manutenzione pianificata")) setPlanOpen(false);
  };
  const saveDone = async () => {
    const body = { ...done, km: done.km || null, next_due_date: done.next_due_date || null, next_due_km: done.next_due_km || null };
    if (await call(() => api.post(`/fleet/maintenance/${doneFor.id}/done`, body), "Manutenzione registrata")) setDoneFor(null);
  };
  const write = can("fleet:write");
  const planFields = [
    { key: "vehicle_id", label: "Mezzo", type: "select", options: vehicleOpts(vehicles), wide: true }, { key: "kind", label: "Tipo", type: "select", options: opts(MAINT_KINDS) },
    { key: "description", label: "Descrizione" }, { key: "due_date", label: "Scadenza (data)", type: "date" }, { key: "due_km", label: "Scadenza (km)", type: "number" },
  ];
  const doneFields = [
    { key: "done_date", label: "Data esecuzione", type: "date" }, { key: "km", label: "Km al momento", type: "number" }, { key: "cost", label: "Costo (€)", type: "number" },
    { key: "supplier", label: "Officina / fornitore" }, { key: "notes", label: "Note", wide: true },
    { key: "next_due_date", label: "Prossima scadenza (data)", type: "date" }, { key: "next_due_km", label: "Prossima scadenza (km)", type: "number" },
  ];
  return (
    <div data-testid="maintenance-tab">
      <div className="mb-3 flex justify-end">
        {write && <Button onClick={() => { setPlan({ vehicle_id: "", kind: "TAGLIANDO", description: "", due_date: "", due_km: 0 }); setPlanOpen(true); }} data-testid="maintenance-add-button"><Plus className="mr-1 h-4 w-4" />Pianifica manutenzione</Button>}
      </div>
      {rows.length === 0 ? <Empty text="Nessuna manutenzione" testId="maintenance-empty" /> : (
        <div className="overflow-x-auto border bg-white">
          <Table>
            <TableHeader><TableRow className="bg-muted/50"><TableHead>Mezzo</TableHead><TableHead>Tipo</TableHead><TableHead>Scadenza</TableHead><TableHead>Km attuali</TableHead><TableHead>Stato</TableHead><TableHead>Esecuzione</TableHead><TableHead /></TableRow></TableHeader>
            <TableBody>
              {rows.map((m) => (
                <TableRow key={m.id} data-testid={`maintenance-row-${m.id}`}>
                  <TableCell className="text-sm"><span className="font-mono font-bold">{m.plate}</span> <span className="text-muted-foreground">{m.model}</span></TableCell>
                  <TableCell className="text-sm">{MAINT_KINDS[m.kind]}{m.description ? ` · ${m.description}` : ""}</TableCell>
                  <TableCell className="font-mono text-xs">{m.due_date ? fmtDate(m.due_date) : ""}{m.due_km ? ` ${Math.round(m.due_km).toLocaleString("it-IT")} km` : ""}</TableCell>
                  <TableCell className="font-mono text-xs">{(m.current_km || 0).toLocaleString("it-IT")}</TableCell>
                  <TableCell className="text-xs font-semibold uppercase">{m.status === "DONE" ? "Eseguita" : "Pianificata"}</TableCell>
                  <TableCell className="text-xs">{m.done ? `${fmtDate(m.done.done_date)} · ${fmtEuro(m.done.cost)} ${m.done.supplier || ""}` : "—"}</TableCell>
                  <TableCell className="whitespace-nowrap text-right">
                    {write && m.status === "PLANNED" && <>
                      <Button size="sm" variant="outline" onClick={() => { setDoneFor(m); setDone({ done_date: isoDate(new Date()), km: m.current_km || 0, cost: 0, supplier: "", notes: "", next_due_date: "", next_due_km: 0 }); }} data-testid={`maintenance-done-${m.id}`}><Check className="mr-1 h-4 w-4" />Eseguita</Button>
                      <Button size="icon" variant="ghost" onClick={() => window.confirm("Eliminare la manutenzione pianificata?") && call(() => api.delete(`/fleet/maintenance/${m.id}`), "Eliminata")} data-testid={`maintenance-delete-${m.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                    </>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      <FormDialog open={planOpen} onClose={() => setPlanOpen(false)} title="Pianifica manutenzione" fields={planFields} form={plan} setForm={setPlan} onSave={savePlan} testPrefix="maintenance" />
      <FormDialog open={!!doneFor} onClose={() => setDoneFor(null)} title={`Manutenzione eseguita · ${doneFor?.plate || ""}`} fields={doneFields} form={done} setForm={setDone} onSave={saveDone} testPrefix="maintenance-done" />
    </div>
  );
};
